import React, { useEffect, useRef, useState } from "react";

/**
 * Konami Code Easter egg.
 * On ↑ ↑ ↓ ↓ ← → ← → B A: fires a golden particle burst and dispatches
 * `cq:open-secret` — SecretAccessModal picks that up.
 *
 * Sequence tracking is buffer-based (last 10 keys) so accidental prefixes
 * don't reset progress.
 */
const KONAMI = [
  "ArrowUp", "ArrowUp",
  "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight",
  "ArrowLeft", "ArrowRight",
  "b", "a",
];

export default function KonamiCode() {
  const bufRef = useRef([]);
  const [burst, setBurst] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      // Don't hijack typing inside inputs/textareas
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;

      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      const buf = bufRef.current;
      buf.push(key);
      if (buf.length > KONAMI.length) buf.shift();

      if (buf.length === KONAMI.length && buf.every((k, i) => k === KONAMI[i])) {
        bufRef.current = [];
        setBurst(true);
        // Give the burst ~700ms of drama before the modal opens
        setTimeout(() => {
          window.dispatchEvent(new Event("cq:open-secret"));
        }, 650);
        setTimeout(() => setBurst(false), 1400);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!burst) return null;

  return (
    <div
      className="fixed inset-0 z-[95] pointer-events-none flex items-center justify-center"
      aria-hidden="true"
      data-testid="konami-burst"
    >
      {/* Radial flash */}
      <div className="konami-flash absolute inset-0" />
      {/* Concentric shockwaves */}
      <div className="konami-ring konami-ring-1" />
      <div className="konami-ring konami-ring-2" />
      <div className="konami-ring konami-ring-3" />
      {/* Particles */}
      {[...Array(48)].map((_, i) => {
        const angle = (i / 48) * Math.PI * 2;
        const dist = 220 + (i % 5) * 40;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        return (
          <span
            key={i}
            className="konami-spark"
            style={{
              // eslint-disable-next-line
              "--dx": `${dx}px`,
              "--dy": `${dy}px`,
              animationDelay: `${(i % 8) * 0.02}s`,
              background:
                i % 3 === 0 ? "#fef3c7" : i % 3 === 1 ? "#fbbf24" : "#f97316",
            }}
          />
        );
      })}

      <style>{`
        .konami-flash {
          background: radial-gradient(30% 30% at 50% 50%, rgba(251,191,36,0.55), transparent 70%);
          animation: k-flash 700ms cubic-bezier(0.22,1,0.36,1) both;
          mix-blend-mode: screen;
        }
        @keyframes k-flash {
          0%   { opacity: 0; transform: scale(0.6); }
          30%  { opacity: 1; transform: scale(1.05); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        .konami-ring {
          position: absolute;
          width: 40px; height: 40px;
          border-radius: 999px;
          border: 3px solid rgba(251,191,36,0.9);
          box-shadow: 0 0 30px rgba(251,191,36,0.6);
          animation: k-ring 900ms cubic-bezier(0.22,1,0.36,1) both;
        }
        .konami-ring-1 { animation-delay: 0ms; }
        .konami-ring-2 { animation-delay: 120ms; border-color: rgba(255,255,255,0.9); }
        .konami-ring-3 { animation-delay: 240ms; border-color: rgba(249,115,22,0.9); }
        @keyframes k-ring {
          0%   { opacity: 0.9; transform: scale(0.2); border-width: 6px; }
          70%  { opacity: 0.6; }
          100% { opacity: 0;   transform: scale(14);  border-width: 1px; }
        }
        .konami-spark {
          position: absolute;
          width: 6px; height: 6px;
          border-radius: 999px;
          box-shadow: 0 0 10px currentColor;
          animation: k-spark 1200ms cubic-bezier(0.22,1,0.36,1) both;
        }
        @keyframes k-spark {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.4); }
          20%  { opacity: 1; transform: translate(calc(var(--dx) * 0.2), calc(var(--dy) * 0.2)) scale(1.4); }
          100% { opacity: 0; transform: translate(var(--dx), var(--dy)) scale(0.2); }
        }
      `}</style>
    </div>
  );
}
